import {SegmentedControl, ActionList, ActionMenu, IconButton, Popover, Heading, Stack, Button} from '@primer/react'
import {use, useId, useRef, useState, type SetStateAction} from 'react'
import {CommentsContext} from './CommentsContext'
import {useMediaQuery} from '@github-ui/use-media-query'
import {FilterIcon, SortAscIcon, SortDescIcon, ListUnorderedIcon} from '@primer/octicons-react'

import styles from './CommentsViewControls.module.css'

import {GitHubAvatar} from '@github-ui/github-avatar'
import {FeatureFlags} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {isDefaultFilterState, type CommentsFilterState} from './use-comments-filter-state'
import {CommentsSort, supportsCodeOrderSort} from './use-comments-sort-state'
import {ViewMode} from './utils/view-mode'
import {displayModeForViewMode, type CommentsDisplayMode} from './utils/display-mode'
import {useAuthorFilterItems} from './use-author-filter-items'
import {useIsNoticeDismissed} from '../../stores/layout-selectors'
import {useDismissNotice} from '../../mutations/use-dismiss-notice'
import {useReturnFocus, type Focusable} from '@github-ui/use-return-focus'

interface CommentsViewControlsProps {
  /** Current filter state, for the current view mode. */
  filterState: CommentsFilterState
  setFilterState: (state: SetStateAction<CommentsFilterState>) => void
  sort: CommentsSort
  setSort: (sort: SetStateAction<CommentsSort>) => void
  viewMode: ViewMode
  displayMode: CommentsDisplayMode
  className?: string
}

function FilterMenu({
  filterState,
  setFilterState,
  displayMode,
}: Pick<CommentsViewControlsProps, 'filterState' | 'setFilterState' | 'displayMode'>) {
  const {viewMode} = use(CommentsContext)

  const toggleResolvedFilter = () => setFilterState(prev => ({...prev, hideResolvedThreads: !prev.hideResolvedThreads}))

  const toggleOutdatedFilter = () => setFilterState(prev => ({...prev, hideOutdatedThreads: !prev.hideOutdatedThreads}))

  const toggleHiddenFilter = () => setFilterState(prev => ({...prev, hideHiddenComments: !prev.hideHiddenComments}))

  const toggleAgents = () => setFilterState(prev => ({...prev, showAgents: !(prev.showAgents ?? true)}))

  const authors = useAuthorFilterItems({filterState, displayMode})

  const onSelectAuthor = (authorLogin: string) => {
    setFilterState(previous => {
      const selectedAuthors = new Set(previous.selectedAuthors ?? [])
      if (selectedAuthors.has(authorLogin)) selectedAuthors.delete(authorLogin)
      else selectedAuthors.add(authorLogin)

      return {
        ...previous,
        selectedAuthors: selectedAuthors.size === 0 ? undefined : [...selectedAuthors],
      }
    })
  }

  const isFilteringActive = !isDefaultFilterState(viewMode, filterState)

  return (
    <div className="position-relative">
      <ActionMenu>
        <ActionMenu.Anchor>
          <IconButton
            icon={FilterIcon}
            aria-label={
              isFilteringActive ? 'Additional comment filters (filters applied)' : 'Additional comment filters'
            }
            description="Additional comment filters"
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay width="small">
          <ActionList>
            <ActionList.Group selectionVariant="multiple">
              <ActionList.Item
                selected={!filterState.hideResolvedThreads}
                onSelect={event => {
                  event.preventDefault()
                  toggleResolvedFilter()
                }}
              >
                Show resolved comments
              </ActionList.Item>
              <ActionList.Item
                selected={!filterState.hideOutdatedThreads}
                onSelect={event => {
                  event.preventDefault()
                  toggleOutdatedFilter()
                }}
              >
                Show outdated comments
              </ActionList.Item>
              <ActionList.Item
                selected={!filterState.hideHiddenComments}
                onSelect={event => {
                  event.preventDefault()
                  toggleHiddenFilter()
                }}
              >
                Show hidden comments
              </ActionList.Item>
              <ActionList.Item
                selected={filterState.showAgents !== false}
                onSelect={event => {
                  event.preventDefault()
                  toggleAgents()
                }}
              >
                Show AI and bot comments
              </ActionList.Item>
              <ActionList.Divider />
            </ActionList.Group>
            <ActionList.Group selectionVariant="multiple">
              <ActionList.GroupHeading>Filter comments by author</ActionList.GroupHeading>

              {authors.map(author => (
                <ActionList.Item
                  key={author.login}
                  selected={filterState.selectedAuthors?.includes(author.login) ?? false}
                  onSelect={event => {
                    event.preventDefault()
                    onSelectAuthor(author.login)
                  }}
                >
                  <ActionList.LeadingVisual>
                    <GitHubAvatar src={author.avatarUrl} />
                  </ActionList.LeadingVisual>
                  {author.login}
                </ActionList.Item>
              ))}
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {isFilteringActive && <span className={styles.filterIndicator} aria-hidden="true" />}
    </div>
  )
}

const sortConfigs = {
  [CommentsSort.NewestFirst]: {
    shortLabel: 'Newest',
    label: 'Newest first',
    Icon: SortDescIcon,
  },
  [CommentsSort.OldestFirst]: {
    shortLabel: 'Oldest',
    label: 'Oldest first',
    Icon: SortAscIcon,
  },
  [CommentsSort.DiffOrder]: {
    shortLabel: 'Code order',
    label: 'Code order',
    Icon: ListUnorderedIcon,
  },
} as const

const SORT_NOTICE_NAME = 'overview_comments_sort' as const

function SortMenu({
  sort: currentSort,
  setSort,
  viewMode,
}: Pick<CommentsViewControlsProps, 'sort' | 'setSort' | 'viewMode'>) {
  const {Icon: CurrenSortIcon, shortLabel: currentSortLabel} = sortConfigs[currentSort]
  const sortAriaLabel = `Sort comments: ${currentSortLabel}`
  const isSmall = useMediaQuery('(max-width: 543px)')

  const availableSorts = supportsCodeOrderSort(displayModeForViewMode(viewMode))
    ? [CommentsSort.NewestFirst, CommentsSort.OldestFirst, CommentsSort.DiffOrder]
    : [CommentsSort.NewestFirst, CommentsSort.OldestFirst]

  const [open, setOpen] = useState(false)

  const sortNoticeId = useId()
  const {surface} = use(CommentsContext)

  const anchorRef = useRef<HTMLButtonElement>(null)

  return (
    <>
      {/* We have to apply anchor-name to a wrapper span because ActionMenu anchor already has its own anchor-name */}
      <span className={styles.sortNoticeAnchor}>
        <ActionMenu onOpenChange={setOpen} open={open} anchorRef={anchorRef}>
          {isSmall ? (
            <ActionMenu.Anchor>
              <IconButton icon={CurrenSortIcon} aria-label={sortAriaLabel} aria-describedby={sortNoticeId} />
            </ActionMenu.Anchor>
          ) : (
            <ActionMenu.Button
              leadingVisual={CurrenSortIcon}
              aria-label={sortAriaLabel}
              aria-describedby={sortNoticeId}
            >
              {currentSortLabel}
            </ActionMenu.Button>
          )}

          <ActionMenu.Overlay>
            <ActionList selectionVariant="single">
              {availableSorts.map(sort => {
                const {label, Icon} = sortConfigs[sort]
                return (
                  <ActionList.Item key={sort} selected={sort === currentSort} onSelect={() => setSort(sort)}>
                    <ActionList.LeadingVisual>
                      <Icon />
                    </ActionList.LeadingVisual>
                    {label}
                  </ActionList.Item>
                )
              })}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </span>
      {surface === 'page' && currentSort === 'newest-first' && (
        <NewestFirstSortNotice id={sortNoticeId} onOpenMenu={() => setOpen(true)} returnFocusRef={anchorRef} />
      )}
    </>
  )
}

function NewestFirstSortNotice({
  id,
  onOpenMenu,
  returnFocusRef,
}: {
  id: string
  onOpenMenu(): void
  returnFocusRef: React.RefObject<Focusable | null>
}) {
  const isNoticeDismissed = useIsNoticeDismissed(SORT_NOTICE_NAME)
  const {dismissNotice} = useDismissNotice(SORT_NOTICE_NAME)

  const [isOpen, setIsOpen] = useState(!isNoticeDismissed)

  const onDismiss = () => {
    // dismissNotice works async, so we also track local open state for an immediate close
    setIsOpen(false)
    dismissNotice()
  }

  const {containerRef} = useReturnFocus(returnFocusRef)

  if (!isOpen) return null

  return (
    <Popover className={styles.sortNotice} open caret="top-right" as="aside" id={id} ref={containerRef}>
      <Popover.Content onEscape={onDismiss}>
        <Heading as="h3" variant="small" className="mb-2">
          Newest-first sort
        </Heading>
        <p>The latest comments are now shown first, bringing the newest activity to the top.</p>
        <p>If you prefer, you can use the sort menu to change back to showing the oldest comments first.</p>
        <Stack direction="horizontal" className="mt-3" gap="condensed">
          <Button
            onClick={() => {
              onDismiss()
              onOpenMenu()
            }}
          >
            Open sort menu
          </Button>
          <Button onClick={() => onDismiss()}>Dismiss</Button>
        </Stack>
      </Popover.Content>
    </Popover>
  )
}

function CommentsViewSwitch() {
  const {viewMode, setViewMode} = use(CommentsContext)

  return (
    <SegmentedControl
      aria-label="Comments view mode"
      onChange={(value: number) => {
        const nextMode = [ViewMode.IssueComments, ViewMode.ThreadPreviews, ViewMode.Mine][value]
        if (nextMode) setViewMode(nextMode)
      }}
    >
      <SegmentedControl.Button selected={viewMode === ViewMode.IssueComments}>All</SegmentedControl.Button>
      <SegmentedControl.Button selected={viewMode === ViewMode.ThreadPreviews}>Unresolved</SegmentedControl.Button>
      <SegmentedControl.Button selected={viewMode === ViewMode.Mine}>Mine</SegmentedControl.Button>
    </SegmentedControl>
  )
}

/**
 * Renders view controls (filter, sort, etc) for comments.
 */
export function CommentsViewControls(props: CommentsViewControlsProps) {
  return (
    <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>
      <div className={clsx(styles.container, props.className)}>
        <CommentsViewSwitch />
        <div className={styles.controls}>
          <FilterMenu {...props} />
          <SortMenu {...props} />
        </div>
      </div>
    </FeatureFlags>
  )
}
